import { DEFAULT_RECOMMENDATION_FILTERS, savedFilterPreferences } from './recommendationFilters';
import { markOnboardingDeclinedFlag } from './authManager';
import { rememberSelectedCity } from './selectedCity';

export const MIN_ONBOARDING_ARTISTS = 3;
// Upper bound on favorite artists added during onboarding. Mirrors the backend
// MAX_ARTISTS_BATCH cap on POST /users/artists/batch so the single batch call
// never exceeds what the API accepts.
export const MAX_ONBOARDING_ARTISTS = 10;
export const MIN_ONBOARDING_THEMES = 3;
export const ONBOARDING_ROUTE_STEPS = ['themes', 'artists'];

const PREFERENCES_KEY = 'user_preferences';

/**
 * Record a decline at the consent gate and give HomeRedirect the minimum it
 * needs to route the visitor to the city listing: the decline marker plus a
 * `user_preferences` holding the city.
 *
 * The marker is written INSTEAD of `onboarding_completed` — marking a decliner
 * as fully onboarded would hide them from the "get events picked for you"
 * cohort. Without any marker, HomeRedirect's
 * `!onboarding_completed || !user_preferences` check would bounce them back
 * into onboarding on every single visit and they'd never reach the banner.
 * It carries no unique identifier, so it needs no consent of its own.
 *
 * @param {{city: string, country?: string}} location
 * @returns {Object|null} The persisted preferences, or null without a city.
 */
export const markOnboardingDeclined = ({ city, country } = {}) => {
  const persisted = persistOnboardingPreferences(
    {
      city: String(city || '').trim(),
      ...(country ? { country } : {}),
      filters: savedFilterPreferences(DEFAULT_RECOMMENDATION_FILTERS),
    },
    { markCompleted: false, preserveExistingFilters: true }
  );

  if (persisted) markOnboardingDeclinedFlag();

  return persisted;
};

export const getArtistId = (artist) => artist?.mbid || artist?.id;

export const refsToThemeValues = (refs = []) =>
  (Array.isArray(refs) ? refs : [])
    .map((ref) => {
      if (typeof ref === 'string') return ref;
      return ref?.name || ref?.id || '';
    })
    .map((value) => String(value || '').trim())
    .filter(Boolean);

export const normalizeFavoriteArtists = (artists = []) =>
  (Array.isArray(artists) ? artists : [])
    .filter((artist) => getArtistId(artist) && artist?.name)
    .map((artist) => ({ id: getArtistId(artist), name: artist.name }));

/**
 * The location the visitor picked on the city screen, in the same shape
 * `getOnboardingLocation(profile)` returns from the backend profile side.
 *
 * These two localStorage keys are the local source of truth for it, and the
 * "drop `country` when absent" rule has to match what the API accepts — so both
 * live here rather than being restated at each read site.
 *
 * @returns {{city: string, country?: string}}
 */
export const getStoredOnboardingLocation = () => {
  const city = localStorage.getItem('lastSelectedCity') || '';
  const country = localStorage.getItem('lastSelectedCountry') || '';
  return { city, ...(country ? { country } : {}) };
};

export const getOnboardingAnswers = (profile) => profile?.answers || {};

export const getOnboardingLocation = (profile) => {
  const location = getOnboardingAnswers(profile).location || {};
  const city = String(location.city || '').trim();
  const country = String(location.country || '').trim();
  return {
    city,
    ...(country ? { country } : {}),
  };
};

export const getOnboardingThemes = (profile) =>
  refsToThemeValues(getOnboardingAnswers(profile).themes?.themes_subjects);

export const hasValidOnboardingLocation = (profile) => Boolean(getOnboardingLocation(profile).city);
export const hasValidOnboardingThemes = (profile) => getOnboardingThemes(profile).length >= MIN_ONBOARDING_THEMES;

/**
 * Both of these judge a *completed* profile on its own answers only, never on how
 * many artists are currently visible. `GET /users/artists` returns favorites, and
 * an artist the user only played passively is deliberately not one (API issue
 * #479) — so a finished profile whose list has since shrunk must not be dragged
 * back through onboarding on every visit. An unfinished profile still owes the
 * artists step whatever its list holds, which is why neither takes `artists`.
 */
export const isCompletedValidOnboarding = (profile) =>
  profile?.status === 'completed' && hasValidOnboardingLocation(profile) && hasValidOnboardingThemes(profile);

export const getNextIncompleteOnboardingStep = (profile) => {
  if (!hasValidOnboardingLocation(profile)) return 'location';
  if (!hasValidOnboardingThemes(profile)) return 'themes';
  if (profile?.status !== 'completed') return 'artists';
  return null;
};

export const getOnboardingStepIndex = (step) => ONBOARDING_ROUTE_STEPS.indexOf(step);

export const buildPreferencesFromOnboarding = (profile, artists = []) => {
  const location = getOnboardingLocation(profile);
  if (!location.city) return null;

  const themes = getOnboardingThemes(profile);

  return {
    city: location.city,
    ...(location.country ? { country: location.country } : {}),
    filters: savedFilterPreferences(DEFAULT_RECOMMENDATION_FILTERS),
    themes,
    artists: normalizeFavoriteArtists(artists).map((artist) => ({
      mbid: artist.id,
      name: artist.name,
    })),
    onboarding_completed: true,
  };
};

/**
 * Persist onboarding-derived preferences to localStorage.
 *
 * `markCompleted` is what separates a finished onboarding from a declined one:
 * a decliner gets the same city/filters persistence but must NOT be recorded as
 * having completed onboarding (see `markOnboardingDeclined`).
 *
 * `preserveExistingFilters` is used by load-time callers (HomeRedirect, OAuth,
 * resuming a completed onboarding) that rebuild preferences from the backend
 * onboarding profile. Without it they would overwrite the user's live filter
 * edits with onboarding defaults on every page load. When set, any `filters`
 * already saved in localStorage (and the mirrored intents/audience) are
 * kept; only the non-filter fields (city, themes, artists) are refreshed from
 * onboarding. Genuine onboarding completion (`submit`) leaves it false so the
 * just-made selections become the new baseline.
 *
 * Returns the object actually persisted so callers can build recommendation
 * options from the same (possibly merged) preferences.
 */
export const persistOnboardingPreferences = (
  preferences,
  { preserveExistingFilters = false, markCompleted = true } = {}
) => {
  if (!preferences?.city) return null;

  let toPersist = preferences;

  if (preserveExistingFilters) {
    try {
      const saved = JSON.parse(localStorage.getItem(PREFERENCES_KEY) || '{}');
      if (saved?.filters) {
        toPersist = {
          ...preferences,
          filters: saved.filters,
          ...(saved.intents !== undefined ? { intents: saved.intents } : {}),
          ...(saved.audience !== undefined ? { audience: saved.audience } : {}),
        };
      }
    } catch {
      // Corrupted/absent saved prefs — fall back to onboarding-derived values.
    }
  }

  localStorage.setItem(PREFERENCES_KEY, JSON.stringify(toPersist));
  if (markCompleted) {
    localStorage.setItem('onboarding_completed', 'true');
  }

  // `lastSelectedCity`/`lastSelectedCountry` are the live, authoritative choice (see
  // getPreferredCity). `preserveExistingFilters` callers rebuild `preferences` from the
  // backend onboarding profile, which still holds the city/country picked *during*
  // onboarding and is never updated after. Overwriting the live selection with that stale
  // pair on every such reload (e.g. HomeRedirect on every "back to feed") is what silently
  // reverted the picker to the onboarding city. Both keys are skipped together:
  // rememberSelectedCity keeps them a pair, and refreshing either one alone from the
  // stale profile would break it.
  const hasLiveCity = preserveExistingFilters && localStorage.getItem('lastSelectedCity');
  if (!hasLiveCity) {
    rememberSelectedCity(toPersist.city, toPersist.country);
  }

  return toPersist;
};
